import type { GetNavigationQueryResult } from "@/sanity.types";

type NavigationItems = NonNullable<
  NonNullable<GetNavigationQueryResult>["items"]
>;

/** One element of `items` from `getNavigationQuery` */
export type NavigationMenuItem = NavigationItems[number];

/** Top-level `navigation` row from `getNavigationQuery` when the item is a group */
export type NavigationMenuGroupItem = Extract<
  NavigationMenuItem,
  { _type: "navigationGroup" }
>;

/** Nav row with a direct `link` (not a nested group) */
export type NavigationMenuLeafItem = Exclude<
  NavigationMenuItem,
  NavigationMenuGroupItem
>;

/** Child of a mega-menu group: a link or one nested column group */
export type NavigationMenuGroupChild = NonNullable<
  NavigationMenuGroupItem["items"]
>[number];

export type NavigationMenuNestedGroup = Extract<
  NavigationMenuGroupChild,
  { _type: "navigationGroup" }
>;

export type NavigationMenuNestedLeaf = Exclude<
  NavigationMenuGroupChild,
  NavigationMenuNestedGroup
>;

export const isNavigationMenuGroupItem = (
  item: NavigationMenuItem,
): item is NavigationMenuGroupItem => item._type === "navigationGroup";

export const isNavigationMenuLeafItem = (
  item: NavigationMenuItem,
): item is NavigationMenuLeafItem => item._type !== "navigationGroup";

export const isNavigationMenuNestedGroup = (
  item: NavigationMenuGroupChild,
): item is NavigationMenuNestedGroup => item._type === "navigationGroup";

export const isNavigationMenuNestedLeaf = (
  item: NavigationMenuGroupChild,
): item is NavigationMenuNestedLeaf =>
  item._type === "navigationItem" || "link" in item;

type NavigationLinkFields = {
  _type: "navigationItem";
  _key?: string;
  label?: string | null;
  link: {
    url?: string | null;
    queryString?: string | null;
    openInNewTab?: boolean | null;
    linkType?: string | null;
  };
};

export const hasNavigationLink = (item: {
  _type?: string;
}): item is NavigationLinkFields =>
  item._type !== "navigationGroup" && "link" in item;
